import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Typography, Space, Divider, Tabs } from 'antd';
import { UserOutlined, LockOutlined, SoundOutlined, SafetyOutlined, MailOutlined, UserAddOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser } from '../store/slices/authSlice';
import axios from 'axios';

const { Title, Text } = Typography;

function Login() {
  const dispatch = useDispatch();
  const { isLoading, error } = useSelector(state => state.auth);
  const [registerLoading, setRegisterLoading] = useState(false);

  const onFinish = async (values) => {
    const result = await dispatch(loginUser({
      username: values.username,
      password: values.password
    }));
    
    if (loginUser.fulfilled.match(result)) {
      message.success('Login successful!');
    } else {
      message.error(result.payload || 'Login failed');
    }
  };

  const onRegister = async (values) => {
    setRegisterLoading(true);
    try {
      const response = await axios.post('/api/auth/register', {
        username: values.username,
        email: values.email,
        password: values.password
      });
      
      message.success('Registration successful! Please login with your credentials.');
      
      // Reset form after successful registration
      const form = document.querySelector('form[data-form="register"]');
      if (form) {
        form.reset();
      }
    } catch (error) {
      message.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-content">
          <Card className="login-form">
            <div className="login-header">
              <div className="login-icon">
                <SoundOutlined />
              </div>
              <Title level={2} className="login-title">
                Social Media Analytics
              </Title>
              <Text type="secondary" className="login-subtitle">
                Secure access to your social media analytics platform
              </Text>
            </div>
            
            <Tabs
              defaultActiveKey="login"
              items={[
                {
                  key: 'login',
                  label: 'Sign In',
                  children: (
                    <Form
                      name="login"
                      onFinish={onFinish}
                      autoComplete="off"
                      size="large"
                      layout="vertical"
                    >
                      <Form.Item
                        name="username"
                        label="Username"
                        rules={[
                          {
                            required: true,
                            message: 'Please input your username!',
                          },
                        ]}
                      >
                        <Input 
                          prefix={<UserOutlined />} 
                          placeholder="Enter your username" 
                          className="login-input"
                        />
                      </Form.Item>

                      <Form.Item
                        name="password"
                        label="Password"
                        rules={[
                          {
                            required: true,
                            message: 'Please input your password!',
                          },
                        ]}
                      >
                        <Input.Password 
                          prefix={<LockOutlined />} 
                          placeholder="Enter your password" 
                          className="login-input"
                        />
                      </Form.Item>

                      <Form.Item>
                        <Button 
                          type="primary" 
                          htmlType="submit" 
                          loading={isLoading}
                          className="login-button"
                          icon={<SafetyOutlined />}
                        >
                          Sign In
                        </Button>
                      </Form.Item>
                    </Form>
                  ),
                },
                {
                  key: 'register',
                  label: 'Sign Up',
                  children: (
                    <Form
                      name="register"
                      onFinish={onRegister}
                      autoComplete="off"
                      size="large"
                      layout="vertical"
                      data-form="register"
                    >
                      <Form.Item
                        name="username"
                        label="Username"
                        rules={[
                          {
                            required: true,
                            message: 'Please input your username!',
                          },
                          {
                            min: 3,
                            message: 'Username must be at least 3 characters!',
                          },
                        ]}
                      >
                        <Input 
                          prefix={<UserOutlined />} 
                          placeholder="Enter your username" 
                          className="login-input"
                        />
                      </Form.Item>

                      <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                          {
                            required: true,
                            message: 'Please input your email!',
                          },
                          {
                            type: 'email',
                            message: 'Please input a valid email!',
                          },
                          {
                            pattern: /^[a-zA-Z0-9._%+-]+@gmail\.com$/,
                            message: 'Email must be a Gmail address (@gmail.com)!',
                          },
                        ]}
                      >
                        <Input 
                          prefix={<MailOutlined />} 
                          placeholder="Enter your Gmail address" 
                          className="login-input"
                        />
                      </Form.Item>

                      <Form.Item
                        name="password"
                        label="Password"
                        rules={[
                          {
                            required: true,
                            message: 'Please input your password!',
                          },
                          {
                            min: 6,
                            message: 'Password must be at least 6 characters!',
                          },
                        ]}
                      >
                        <Input.Password 
                          prefix={<LockOutlined />} 
                          placeholder="Enter your password" 
                          className="login-input"
                        />
                      </Form.Item>

                      <Form.Item
                        name="confirmPassword"
                        label="Confirm Password"
                        dependencies={['password']}
                        rules={[
                          {
                            required: true,
                            message: 'Please confirm your password!',
                          },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue('password') === value) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error('Passwords do not match!'));
                            },
                          }),
                        ]}
                      >
                        <Input.Password 
                          prefix={<LockOutlined />} 
                          placeholder="Confirm your password" 
                          className="login-input"
                        />
                      </Form.Item>

                      <Form.Item>
                        <Button 
                          type="primary" 
                          htmlType="submit" 
                          loading={registerLoading}
                          className="login-button"
                          icon={<UserAddOutlined />}
                        >
                          Sign Up
                        </Button>
                      </Form.Item>
                    </Form>
                  ),
                },
              ]}
            />
            
            <Divider />
            
            <div className="login-footer">
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                  <SafetyOutlined /> Secure Authentication
                </Text>
                <Text type="secondary" style={{ textAlign: 'center', display: 'block', fontSize: '12px' }}>
                  Protected by advanced security measures
                </Text>
              </Space>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Login;